/**
 * Performance Optimization Utilities
 * 
 * Provides performance enhancement functions:
 * - Image lazy loading
 * - Data caching
 * - Debounce/throttle utilities
 * - Intersection Observer hooks
 * - Memory-efficient data structures
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// ============================================================================
// CACHING UTILITIES
// ============================================================================

/**
 * Simple in-memory cache with TTL
 */
class MemoryCache {
  constructor(defaultTTL = 5 * 60 * 1000) { // 5 minutes default
    this.cache = new Map();
    this.defaultTTL = defaultTTL;
  }

  set(key, value, ttl = this.defaultTTL) {
    const expiry = Date.now() + ttl;
    this.cache.set(key, { value, expiry });
    return this;
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.value;
  }

  has(key) {
    return this.get(key) !== null;
  }

  delete(key) {
    return this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }

  // Clean up expired entries
  cleanup() {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    }
  }
}

// Shared cache instance
export const cache = new MemoryCache();

// Periodic cleanup (every 5 minutes)
setInterval(() => cache.cleanup(), 5 * 60 * 1000);

/**
 * Cache data in sessionStorage with TTL
 */
export function sessionCache(key, data, ttlMinutes = 30) {
  const item = {
    data,
    expiry: Date.now() + ttlMinutes * 60 * 1000
  };
  try {
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (e) {
    // Storage full, clear old items
    sessionStorage.clear();
    sessionStorage.setItem(key, JSON.stringify(item));
  }
}

/**
 * Get data from sessionStorage with TTL check
 */
export function getSessionCache(key) {
  try {
    const itemStr = sessionStorage.getItem(key);
    if (!itemStr) return null;
    
    const item = JSON.parse(itemStr);
    if (Date.now() > item.expiry) {
      sessionStorage.removeItem(key);
      return null;
    }
    
    return item.data;
  } catch {
    return null;
  }
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook for caching data with automatic invalidation
 */
export function useCachedData(key, fetchFn, ttl = 5 * 60 * 1000) {
  const [data, setData] = useState(() => cache.get(key));
  const [loading, setLoading] = useState(!cache.has(key));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchFn();
      cache.set(key, result, ttl);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [key, fetchFn, ttl]);

  useEffect(() => {
    if (!cache.has(key)) {
      refresh();
    }
  }, [key, refresh]);

  const invalidate = useCallback(() => {
    cache.delete(key);
    refresh();
  }, [key, refresh]);

  return { data, loading, error, refresh, invalidate };
}

/**
 * Hook for lazy loading images with intersection observer
 */
export function useLazyImage(src, placeholder = '') {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!src) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Start loading the image
          const img = new Image();
          img.onload = () => {
            setImageSrc(src);
            setIsLoaded(true);
          };
          img.onerror = () => {
            setHasError(true);
          };
          img.src = src;
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before entering viewport
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [src]);

  return { imageSrc, isLoaded, hasError, imgRef };
}

/**
 * Hook for infinite scroll with intersection observer
 */
export function useInfiniteScroll(loadMore, hasMore, options = {}) {
  const observerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        async ([entry]) => {
          if (entry.isIntersecting && hasMore) {
            setIsLoading(true);
            await loadMore();
            setIsLoading(false);
          }
        },
        { rootMargin: options.rootMargin || '100px' }
      );

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [isLoading, hasMore, loadMore, options.rootMargin]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { lastElementRef, isLoading };
}

/**
 * Hook for debouncing values (useful for search inputs)
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook for debouncing callbacks
 */
export function useDebouncedCallback(callback, delay = 300) {
  const timeoutRef = useRef(null);

  const debouncedFn = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
}

/**
 * Hook for throttling callbacks
 */
export function useThrottledCallback(callback, limit = 100) {
  const inThrottle = useRef(false);
  const lastArgs = useRef(null);

  const throttledFn = useCallback(
    (...args) => {
      lastArgs.current = args;
      
      if (!inThrottle.current) {
        callback(...args);
        inThrottle.current = true;
        
        setTimeout(() => {
          inThrottle.current = false;
          if (lastArgs.current !== args) {
            callback(...lastArgs.current);
          }
        }, limit);
      }
    },
    [callback, limit]
  );

  return throttledFn;
}

/**
 * Hook for tracking component visibility
 */
export function useOnScreen(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      options
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return [ref, isVisible];
}

/**
 * Hook for measuring element dimensions
 */
export function useMeasure() {
  const ref = useRef(null);
  const [bounds, setBounds] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0
  });

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setBounds(entry.contentRect);
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, bounds];
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Debounce function
 */
export function debounce(fn, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Throttle function
 */
export function throttle(fn, limit) {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Memoize expensive function calls
 */
export function memoize(fn, keyFn = (...args) => JSON.stringify(args)) {
  const cache = new Map();
  
  return (...args) => {
    const key = keyFn(...args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
}

/**
 * Chunk array into smaller arrays
 */
export function chunkArray(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Request idle callback wrapper
 */
export function whenIdle(callback) {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback);
  }
  return setTimeout(callback, 1);
}

/**
 * Cancel idle callback
 */
export function cancelIdle(id) {
  if ('cancelIdleCallback' in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
}

/**
 * Batch DOM updates
 */
export function batchUpdates(updates) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      updates();
      resolve();
    });
  });
}

/**
 * Preload critical images
 */
export function preloadImages(urls) {
  return Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(url);
          img.onerror = () => reject(url);
          img.src = url;
        })
    )
  );
}

/**
 * Preload component/route
 */
export function preloadComponent(importFn) {
  return importFn();
}

// ============================================================================
// PERFORMANCE MONITORING
// ============================================================================

/**
 * Measure render time
 */
export function useRenderTime(componentName) {
  const startTime = useRef(performance.now());

  useEffect(() => {
    const endTime = performance.now();
    const duration = endTime - startTime.current;
    
    if (duration > 16) { // Over 16ms (one frame)
      console.warn(`[Performance] ${componentName} took ${duration.toFixed(2)}ms to render`);
    }
  });

  return null;
}

/**
 * Track long tasks
 */
export function trackLongTasks(callback) {
  if (!('PerformanceObserver' in window)) return null;

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.duration > 50) { // Over 50ms
        callback(entry);
      }
    }
  });

  observer.observe({ entryTypes: ['longtask'] });
  return () => observer.disconnect();
}

/**
 * Get memory usage (if available)
 */
export function getMemoryUsage() {
  if (performance.memory) {
    return {
      usedJSHeapSize: performance.memory.usedJSHeapSize,
      totalJSHeapSize: performance.memory.totalJSHeapSize,
      jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
    };
  }
  return null;
}

// ============================================================================
// VIRTUAL LIST HOOK
// ============================================================================

/**
 * Hook for virtualizing long lists
 */
export function useVirtualList(items, options = {}) {
  const {
    itemHeight = 50,
    overscan = 5,
    containerHeight = 400
  } = options;

  const [scrollTop, setScrollTop] = useState(0);

  const visibleCount = Math.ceil(containerHeight / itemHeight);
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(items.length - 1, startIndex + visibleCount + overscan * 2);

  const visibleItems = useMemo(() => {
    return items.slice(startIndex, endIndex + 1).map((item, index) => ({
      ...item,
      style: {
        position: 'absolute',
        top: (startIndex + index) * itemHeight,
        height: itemHeight,
        left: 0,
        right: 0
      }
    }));
  }, [items, startIndex, endIndex, itemHeight]);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const totalHeight = items.length * itemHeight;

  return {
    visibleItems,
    handleScroll,
    totalHeight,
    containerStyle: {
      height: containerHeight,
      overflow: 'auto',
      position: 'relative'
    }
  };
}

const performanceUtils = {
  cache,
  sessionCache,
  getSessionCache,
  useCachedData,
  useLazyImage,
  useInfiniteScroll,
  useDebounce,
  useDebouncedCallback,
  useThrottledCallback,
  useOnScreen,
  useMeasure,
  debounce,
  throttle,
  memoize,
  chunkArray,
  whenIdle,
  cancelIdle,
  batchUpdates,
  preloadImages,
  preloadComponent,
  useRenderTime,
  trackLongTasks,
  getMemoryUsage,
  useVirtualList
};

export default performanceUtils;
